import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { InlineEdit } from './InlineEdit';
import { PlanContextMenu } from './PlanContextMenu';
import { useToast } from '../../contexts/ToastContext';
const TYPE_ICONS = {
    'Epic': { bg: 'bg-purple-100', text: 'text-purple-700', letter: 'E' },
    'Story': { bg: 'bg-green-100', text: 'text-green-700', letter: 'S' },
    'Task': { bg: 'bg-blue-100', text: 'text-blue-700', letter: 'T' },
    'Bug': { bg: 'bg-red-100', text: 'text-red-700', letter: 'B' },
    'Sub-task': { bg: 'bg-sky-100', text: 'text-sky-700', letter: 'ST' },
};
const STATUS_COLORS = {
    'new': 'bg-gray-100 text-gray-700',
    'indeterminate': 'bg-blue-100 text-blue-700',
    'done': 'bg-green-100 text-green-700',
};
const PRIORITY_COLORS = {
    'Highest': 'bg-red-100 text-red-700',
    'High': 'bg-orange-100 text-orange-700',
    'Medium': 'bg-yellow-100 text-yellow-700',
    'Low': 'bg-green-100 text-green-700',
    'Lowest': 'bg-gray-100 text-gray-600',
};
const PRIORITY_OPTIONS = [
    { value: 'Highest', label: 'Highest' },
    { value: 'High', label: 'High' },
    { value: 'Medium', label: 'Medium' },
    { value: 'Low', label: 'Low' },
    { value: 'Lowest', label: 'Lowest' },
];
function SortableHeader({ field, label, className, sortField, sortDirection, onSort, }) {
    const isActive = sortField === field;
    return (_jsxs("button", { onClick: () => onSort(field), className: `flex items-center gap-0.5 hover:text-text-primary cursor-pointer ${className || ''} ${isActive ? 'text-primary-600' : ''}`, children: [label, isActive && (_jsx("svg", { className: "w-3 h-3", viewBox: "0 0 12 12", fill: "currentColor", children: sortDirection === 'asc' ? (_jsx("path", { d: "M6 3L10 8H2L6 3Z" })) : (_jsx("path", { d: "M6 9L2 4H10L6 9Z" })) }))] }));
}
export function PlanListView({ epics, unparentedIssues, expandedKeys, onToggleExpand, onReorder, onBulkMoveToEpic, onUpdateField, onAddChild, canEdit, jiraBaseUrl, hasDraft, getDraftStatus, selectedKeys, onToggleSelect, onSelectAll, onClearSelection, onVisualOrderChange, statuses, assignees, onViewDetails, }) {
    const { showToast } = useToast();
    const [contextMenu, setContextMenu] = useState(null);
    const [dragOverKey, setDragOverKey] = useState(null);
    const [dragPosition, setDragPosition] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const dragDataRef = useRef(null);
    const dragGhostRef = useRef(null);
    // Always-current ref so drag handlers can read the latest selection even if
    // React hasn't re-rendered the inline event handler closures yet.
    const selectedKeysRef = useRef(selectedKeys);
    selectedKeysRef.current = selectedKeys;
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const [unparentedSearch, setUnparentedSearch] = useState('');
    // Build a flat lookup map so we can resolve issue metadata during bulk drag
    const issueMap = useMemo(() => {
        const map = new Map();
        const traverse = (issues) => {
            for (const issue of issues) {
                map.set(issue.issueKey, issue);
                traverse(issue.children);
            }
        };
        traverse(epics);
        traverse(unparentedIssues);
        return map;
    }, [epics, unparentedIssues]);
    const handleSort = useCallback((field) => {
        if (sortField === field) {
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            }
            else {
                // Third click clears sort
                setSortField(null);
                setSortDirection('asc');
            }
        }
        else {
            setSortField(field);
            setSortDirection('asc');
        }
    }, [sortField, sortDirection]);
    const sortIssues = useCallback((issues) => {
        if (!sortField)
            return issues;
        const sorted = [...issues].sort((a, b) => {
            let cmp = 0;
            switch (sortField) {
                case 'key': {
                    // Natural sort: compare project prefix alphabetically, then issue number numerically
                    const matchA = a.issueKey.match(/^(.+)-(\d+)$/);
                    const matchB = b.issueKey.match(/^(.+)-(\d+)$/);
                    if (matchA && matchB) {
                        cmp = matchA[1].localeCompare(matchB[1]);
                        if (cmp === 0) {
                            cmp = parseInt(matchA[2], 10) - parseInt(matchB[2], 10);
                        }
                    }
                    else {
                        cmp = a.issueKey.localeCompare(b.issueKey);
                    }
                    break;
                }
                case 'summary':
                    cmp = a.summary.localeCompare(b.summary);
                    break;
                case 'status':
                    cmp = a.status.localeCompare(b.status);
                    break;
                case 'priority': {
                    const priorityOrder = { 'Highest': 0, 'High': 1, 'Medium': 2, 'Low': 3, 'Lowest': 4 };
                    cmp = (priorityOrder[a.priority || ''] ?? 5) - (priorityOrder[b.priority || ''] ?? 5);
                    break;
                }
                case 'assignee':
                    cmp = (a.assigneeName || '').localeCompare(b.assigneeName || '');
                    break;
                case 'storyPoints':
                    cmp = (a.storyPoints ?? -1) - (b.storyPoints ?? -1);
                    break;
                case 'startDate':
                    cmp = (a.startDate || '').localeCompare(b.startDate || '');
                    break;
                case 'dueDate':
                    cmp = (a.dueDate || '').localeCompare(b.dueDate || '');
                    break;
                case 'progress':
                    cmp = a.progress - b.progress;
                    break;
            }
            return sortDirection === 'asc' ? cmp : -cmp;
        });
        // Recursively sort children
        return sorted.map(issue => ({
            ...issue,
            children: sortIssues(issue.children),
        }));
    }, [sortField, sortDirection]);
    const sortedEpics = useMemo(() => sortIssues(epics), [epics, sortIssues]);
    const sortedUnparented = useMemo(() => sortIssues(unparentedIssues), [unparentedIssues, sortIssues]);
    // Client-side filter for unparented issues search
    const filteredUnparented = useMemo(() => {
        if (!unparentedSearch.trim())
            return sortedUnparented;
        const term = unparentedSearch.trim().toLowerCase();
        return sortedUnparented.filter(issue => issue.issueKey.toLowerCase().includes(term) ||
            issue.summary.toLowerCase().includes(term) ||
            (issue.assigneeName && issue.assigneeName.toLowerCase().includes(term)));
    }, [sortedUnparented, unparentedSearch]);
    // Collect all visible issue keys in visual (sorted + filtered) order
    const allVisibleKeys = useMemo(() => {
        const keys = [];
        const collect = (issues) => {
            for (const issue of issues) {
                keys.push(issue.issueKey);
                if (expandedKeys.has(issue.issueKey)) {
                    collect(issue.children);
                }
            }
        };
        collect(sortedEpics);
        collect(filteredUnparented);
        return keys;
    }, [sortedEpics, filteredUnparented, expandedKeys]);
    const allSelected = allVisibleKeys.length > 0 && selectedKeys ? allVisibleKeys.every(k => selectedKeys.has(k)) : false;
    // Push the visual key order to the parent so shift-select uses the
    // correct sorted/filtered order rather than the unsorted tree order.
    useEffect(() => {
        onVisualOrderChange?.(allVisibleKeys);
    }, [allVisibleKeys, onVisualOrderChange]);
    const handleDragStart = (e, issue) => {
        // Read from the ref to guarantee the latest selection regardless of render timing
        const currentSelected = selectedKeysRef.current;
        const selSize = currentSelected?.size ?? 0;
        const hasKey = currentSelected?.has(issue.issueKey) ?? false;
        const isBulk = selSize > 1 && hasKey;
        const allKeys = isBulk ? Array.from(currentSelected) : [issue.issueKey];
        const data = {
            issueKey: issue.issueKey,
            issueType: issue.issueType,
            parentKey: issue.parentIssueKey,
            allKeys,
        };
        dragDataRef.current = data;
        e.dataTransfer.setData('text/plain', JSON.stringify(data));
        e.dataTransfer.effectAllowed = 'move';
        setIsDragging(true);
        // Create a custom drag ghost showing the count — always show it for bulk drags
        if (isBulk) {
            const ghost = document.createElement('div');
            ghost.style.cssText =
                'position:fixed;top:-1000px;left:-1000px;display:flex;align-items:center;gap:8px;' +
                    'padding:6px 12px;background:#1B3A6B;color:white;border-radius:6px;font-size:13px;' +
                    'font-family:system-ui,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,0.15);white-space:nowrap;';
            ghost.textContent = `Moving ${allKeys.length} issues`;
            document.body.appendChild(ghost);
            e.dataTransfer.setDragImage(ghost, 0, 0);
            dragGhostRef.current = ghost;
        }
    };
    const handleDragEnd = () => {
        setIsDragging(false);
        dragDataRef.current = null;
        if (dragGhostRef.current) {
            document.body.removeChild(dragGhostRef.current);
            dragGhostRef.current = null;
        }
    };
    const handleDragOver = useCallback((e, targetIssue) => {
        e.preventDefault();
        if (!dragDataRef.current)
            return;
        const dragData = dragDataRef.current;
        const draggedKeys = new Set(dragData.allKeys);
        // Prevent dropping on any of the dragged items
        if (draggedKeys.has(targetIssue.issueKey))
            return;
        // Check if ALL dragged items are epics (not just ANY)
        const allEpics = dragData.allKeys.every(k => {
            const issue = issueMap.get(k);
            return issue?.issueType === 'Epic';
        });
        // Only block drop on non-epic targets if ALL items are epics (can't nest epics under stories)
        if (allEpics && targetIssue.issueType !== 'Epic')
            return;
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const height = rect.height;
        // Determine drop position
        let pos;
        if (targetIssue.issueType === 'Epic' && !allEpics) {
            // Dropping items (some or all non-epics) on an epic = nesting/reparenting
            if (y < height * 0.2) {
                pos = 'above';
            }
            else if (y > height * 0.8) {
                pos = 'below';
            }
            else {
                pos = 'on';
            }
        }
        else if (y < height * 0.5) {
            pos = 'above';
        }
        else {
            pos = 'below';
        }
        setDragPosition(pos);
        setDragOverKey(targetIssue.issueKey);
        e.dataTransfer.dropEffect = 'move';
    }, [issueMap]);
    const handleDragLeave = useCallback(() => {
        setDragOverKey(null);
        setDragPosition(null);
    }, []);
    // Not wrapped in useCallback — must always reference the latest closure values
    // so that bulk drag-and-drop picks up current issueMap, onBulkMoveToEpic, etc.
    const handleDrop = async (e, targetIssue, siblings) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverKey(null);
        setDragPosition(null);
        setIsDragging(false);
        if (!dragDataRef.current)
            return;
        const dragData = dragDataRef.current;
        dragDataRef.current = null;
        // Clean up drag ghost
        if (dragGhostRef.current) {
            document.body.removeChild(dragGhostRef.current);
            dragGhostRef.current = null;
        }
        // Use the selection captured at drag-start time (dragData.allKeys) as the
        // primary source of truth. Also check current selection via ref as a fallback.
        const primaryKey = dragData.issueKey;
        const currentSelected = selectedKeysRef.current;
        const dragStartKeys = dragData.allKeys;
        // Prefer dragData.allKeys (captured at dragstart) — it's immune to any
        // mid-drag selection changes.  Fall back to the live ref if dragstart
        // captured only a single key but the ref still shows a multi-selection.
        let effectiveKeys;
        if (dragStartKeys.length > 1) {
            effectiveKeys = dragStartKeys.filter(k => k !== targetIssue.issueKey);
        }
        else if (currentSelected && currentSelected.has(primaryKey) && currentSelected.size > 1) {
            effectiveKeys = Array.from(currentSelected).filter(k => k !== targetIssue.issueKey);
        }
        else {
            effectiveKeys = primaryKey === targetIssue.issueKey ? [] : [primaryKey];
        }
        if (effectiveKeys.length === 0)
            return;
        const draggedKeys = new Set(effectiveKeys);
        // Compute drop position from the drop event coordinates
        const allEpics = effectiveKeys.every(k => {
            const iss = issueMap.get(k);
            return iss?.issueType === 'Epic';
        });
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const height = rect.height;
        let currentDragPosition;
        if (targetIssue.issueType === 'Epic' && !allEpics) {
            if (y < height * 0.2)
                currentDragPosition = 'above';
            else if (y > height * 0.8)
                currentDragPosition = 'below';
            else
                currentDragPosition = 'on';
        }
        else if (y < height * 0.5) {
            currentDragPosition = 'above';
        }
        else {
            currentDragPosition = 'below';
        }
        const isBulk = effectiveKeys.length > 1;
        try {
            // ── Reparent: dropping onto an epic ──
            if (targetIssue.issueType === 'Epic' && currentDragPosition === 'on') {
                const movableKeys = effectiveKeys.filter(k => {
                    const issue = issueMap.get(k);
                    return issue && issue.issueType !== 'Epic';
                });
                if (movableKeys.length === 0)
                    return;
                const keysToMove = movableKeys.filter(k => {
                    const issue = issueMap.get(k);
                    return (issue?.parentIssueKey ?? null) !== targetIssue.issueKey;
                });
                if (keysToMove.length === 0) {
                    showToast('All selected issues are already under this epic', 'info');
                    return;
                }
                showToast(`Moving ${keysToMove.length} issue(s) to ${targetIssue.issueKey}...`, 'info');
                // Build oldEpicKeys map for the bulk API call
                const oldEpicKeys = {};
                for (const key of keysToMove) {
                    const issue = issueMap.get(key);
                    oldEpicKeys[key] = issue?.parentIssueKey ?? null;
                }
                try {
                    const result = await onBulkMoveToEpic(keysToMove, targetIssue.issueKey, oldEpicKeys);
                    const msg = isBulk
                        ? `${result.count} issue(s) moved — submitted for approval`
                        : 'Change submitted for approval';
                    showToast(msg, 'success');
                }
                catch (err) {
                    console.error(`[handleDrop] Bulk move failed:`, err);
                    showToast(`Failed to move issues to ${targetIssue.issueKey}`, 'error');
                }
                onClearSelection?.();
                return;
            }
            // ── Reorder: above / below ──
            const targetIndex = siblings.findIndex(s => s.issueKey === targetIssue.issueKey);
            if (targetIndex === -1)
                return;
            const insertIndex = currentDragPosition === 'above' ? targetIndex : targetIndex + 1;
            const remaining = siblings.filter(s => !draggedKeys.has(s.issueKey));
            const draggedInOrder = siblings.filter(s => draggedKeys.has(s.issueKey));
            if (draggedInOrder.length === 0) {
                const filtered = siblings.filter(s => s.issueKey !== dragData.issueKey);
                const items = filtered.map((s, i) => ({
                    issueKey: s.issueKey,
                    planOrder: i < insertIndex ? i : i + 1,
                }));
                items.push({ issueKey: dragData.issueKey, planOrder: insertIndex });
                items.sort((a, b) => a.planOrder - b.planOrder);
                const reordered = items.map((item, i) => ({ ...item, planOrder: i }));
                await onReorder(reordered);
            }
            else {
                const removedBefore = siblings.slice(0, insertIndex).filter(s => draggedKeys.has(s.issueKey)).length;
                const adjustedIndex = insertIndex - removedBefore;
                const merged = [...remaining];
                merged.splice(adjustedIndex, 0, ...draggedInOrder);
                const reordered = merged.map((s, i) => ({ issueKey: s.issueKey, planOrder: i }));
                await onReorder(reordered);
            }
            onClearSelection?.();
        }
        catch {
            showToast('Failed to reorder', 'error');
        }
    };
    const handleContextMenu = useCallback((e, issue) => {
        e.preventDefault();
        setContextMenu({ issue, position: { x: e.clientX, y: e.clientY } });
    }, []);
    const handleFieldSave = useCallback(async (issueKey, fieldName, oldValue, newValue) => {
        try {
            await onUpdateField(issueKey, fieldName, oldValue, newValue);
            showToast('Change submitted for approval', 'info');
        }
        catch (err) {
            showToast(err instanceof Error ? err.message : 'Failed to update', 'error');
            throw err;
        }
    }, [onUpdateField, showToast]);
    const handleOpenInJira = useCallback((issueKey) => {
        if (jiraBaseUrl) {
            window.open(`${jiraBaseUrl}/browse/${issueKey}`, '_blank');
        }
    }, [jiraBaseUrl]);
    const renderRow = (issue, depth, siblings) => {
        const isExpanded = expandedKeys.has(issue.issueKey);
        const hasChildren = issue.children.length > 0;
        const defaultTypeInfo = { bg: 'bg-blue-100', text: 'text-blue-700', letter: 'T' };
        const typeInfo = TYPE_ICONS[issue.issueType] ?? defaultTypeInfo;
        const statusColor = STATUS_COLORS[issue.statusCategory] || STATUS_COLORS['new'];
        const isDragOver = dragOverKey === issue.issueKey;
        const isActiveSprint = issue.sprintState === 'active';
        const isSelected = selectedKeys?.has(issue.issueKey) || false;
        // Dim selected rows while they are being dragged
        const isBeingDragged = isDragging && isSelected && (selectedKeys?.size ?? 0) > 1;
        let dragIndicatorClass = '';
        if (isDragOver) {
            if (dragPosition === 'above')
                dragIndicatorClass = 'border-t-2 border-t-primary-500';
            else if (dragPosition === 'below')
                dragIndicatorClass = 'border-b-2 border-b-primary-500';
            else if (dragPosition === 'on')
                dragIndicatorClass = 'ring-2 ring-primary-400 bg-primary-50';
        }
        return (_jsxs("div", { children: [_jsxs("div", { className: `flex items-center gap-1 px-2 py-1.5 hover:bg-surface-muted border-b border-border/50 group ${dragIndicatorClass} ${isActiveSprint ? 'border-l-[3px] border-l-blue-500 bg-blue-50' : ''} ${isBeingDragged ? 'opacity-40 bg-primary-50' : ''} ${isSelected && !isDragging ? 'bg-primary-50/50' : ''}`, style: { paddingLeft: `${depth * 24 + 8}px` }, onContextMenu: e => handleContextMenu(e, issue), draggable: canEdit, onDragStart: e => handleDragStart(e, issue), onDragEnd: handleDragEnd, onDragOver: e => handleDragOver(e, issue), onDragLeave: handleDragLeave, onDrop: e => void handleDrop(e, issue, siblings), children: [onToggleSelect && (_jsx("input", { type: "checkbox", checked: selectedKeys?.has(issue.issueKey) || false, onChange: () => { }, onClick: (e) => {
                                e.stopPropagation();
                                onToggleSelect(issue.issueKey, e.shiftKey);
                            }, className: "w-3.5 h-3.5 rounded border-border text-primary-600 focus:ring-primary-500 flex-shrink-0 cursor-pointer" })), canEdit && (_jsx("span", { className: "w-4 text-text-tertiary cursor-grab opacity-0 group-hover:opacity-100 text-xs select-none", children: "\u2630" })), _jsx("button", { onClick: () => onToggleExpand(issue.issueKey), className: `w-4 h-4 flex items-center justify-center text-text-tertiary ${hasChildren ? 'hover:text-text-primary' : 'invisible'}`, children: hasChildren && (_jsx("svg", { className: `w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`, fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M9 5l7 7-7 7" }) })) }), _jsx("span", { className: `w-5 h-5 flex items-center justify-center rounded-sm ${typeInfo.bg} ${typeInfo.text} text-[10px] font-bold flex-shrink-0`, children: typeInfo.letter }), _jsx("button", { draggable: false, className: "text-xs text-text-secondary hover:text-primary-600 hover:underline font-mono flex-shrink-0 w-20 text-left", onClick: e => { e.stopPropagation(); onViewDetails?.(issue.issueKey); }, title: "View details", children: issue.issueKey }), _jsx("div", { className: "flex-1 min-w-0 text-sm overflow-hidden", children: _jsx(InlineEdit, { value: issue.summary, onSave: newVal => handleFieldSave(issue.issueKey, 'summary', issue.summary, newVal), disabled: !canEdit, className: "truncate", hasDraft: hasDraft?.(issue.issueKey, 'summary'), draftStatus: getDraftStatus?.(issue.issueKey, 'summary') }) }), _jsx("div", { className: "w-24 flex-shrink-0 text-center", children: _jsx(InlineEdit, { value: issue.status, onSave: newVal => handleFieldSave(issue.issueKey, 'status', issue.status, newVal), type: "select", options: statuses || [], disabled: !canEdit, className: `inline-flex px-2 py-0.5 text-xs rounded-full font-medium ${statusColor}`, hasDraft: hasDraft?.(issue.issueKey, 'status'), draftStatus: getDraftStatus?.(issue.issueKey, 'status') }) }), _jsx("div", { className: "w-20 flex-shrink-0 text-center", children: _jsx(InlineEdit, { value: issue.priority || '', onSave: newVal => handleFieldSave(issue.issueKey, 'priority', issue.priority ?? null, newVal), type: "select", options: PRIORITY_OPTIONS, disabled: !canEdit, placeholder: "-", className: `inline-flex px-2 py-0.5 text-xs rounded-full font-medium ${PRIORITY_COLORS[issue.priority || ''] || 'bg-gray-50 text-gray-500'}`, hasDraft: hasDraft?.(issue.issueKey, 'priority'), draftStatus: getDraftStatus?.(issue.issueKey, 'priority') }) }), isActiveSprint && (_jsx("span", { className: "px-1.5 py-0.5 text-[10px] rounded bg-blue-100 text-blue-700 font-medium flex-shrink-0", title: issue.sprintName || 'Active Sprint', children: "Sprint" })), _jsx("div", { className: "w-28 flex-shrink-0 text-right", children: _jsx(InlineEdit, { value: issue.assigneeAccountId || '', onSave: newVal => handleFieldSave(issue.issueKey, 'assignee', issue.assigneeAccountId ?? null, newVal), type: "select", options: [
                                    { value: '', label: 'Unassigned' },
                                    ...(assignees || []).map(a => ({ value: a.accountId, label: a.name })),
                                ], disabled: !canEdit, placeholder: "Unassigned", className: "text-xs truncate", hasDraft: hasDraft?.(issue.issueKey, 'assignee'), draftStatus: getDraftStatus?.(issue.issueKey, 'assignee') }) }), _jsx("div", { className: "w-12 flex-shrink-0 text-right", children: _jsx(InlineEdit, { value: issue.storyPoints?.toString() || '', onSave: newVal => handleFieldSave(issue.issueKey, 'story_points', issue.storyPoints?.toString() ?? null, newVal), type: "number", disabled: !canEdit || issue.issueType === 'Epic', placeholder: "-", className: "text-xs text-right", hasDraft: hasDraft?.(issue.issueKey, 'story_points'), draftStatus: getDraftStatus?.(issue.issueKey, 'story_points') }) }), _jsx("div", { className: "w-24 flex-shrink-0 text-right", children: _jsx(InlineEdit, { value: issue.startDate ?? '', onSave: newVal => handleFieldSave(issue.issueKey, 'start_date', issue.startDate ?? null, newVal), type: "date", disabled: !canEdit, placeholder: "-", className: "text-xs", hasDraft: hasDraft?.(issue.issueKey, 'start_date'), draftStatus: getDraftStatus?.(issue.issueKey, 'start_date') }) }), _jsx("div", { className: "w-24 flex-shrink-0 text-right", children: _jsx(InlineEdit, { value: issue.dueDate ?? '', onSave: newVal => handleFieldSave(issue.issueKey, 'due_date', issue.dueDate ?? null, newVal), type: "date", disabled: !canEdit, placeholder: "-", className: "text-xs", hasDraft: hasDraft?.(issue.issueKey, 'due_date'), draftStatus: getDraftStatus?.(issue.issueKey, 'due_date') }) }), hasChildren && (_jsx("div", { className: "w-16 flex-shrink-0", children: _jsxs("div", { className: "flex items-center gap-1", children: [_jsx("div", { className: "flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden", children: _jsx("div", { className: "h-full bg-green-500 rounded-full transition-all", style: { width: `${issue.progress}%` } }) }), _jsxs("span", { className: "text-[10px] text-text-tertiary w-7 text-right", children: [issue.progress, "%"] })] }) })), !hasChildren && _jsx("div", { className: "w-16 flex-shrink-0" }), _jsx("button", { onClick: e => handleContextMenu(e, issue), className: "w-6 h-6 flex items-center justify-center text-text-tertiary hover:text-text-primary opacity-0 group-hover:opacity-100 flex-shrink-0", children: "\u22EE" })] }), isExpanded && hasChildren && (_jsx("div", { children: issue.children.map(child => renderRow(child, depth + 1, issue.children)) }))] }, issue.issueKey));
    };
    return (_jsxs("div", { className: "bg-white rounded-lg border border-border overflow-hidden", children: [_jsxs("div", { className: "flex items-center gap-1 px-2 py-2 bg-surface-muted border-b border-border text-xs font-medium text-text-secondary select-none", children: [onSelectAll && (_jsx("input", { type: "checkbox", checked: allSelected, onChange: () => onSelectAll(allSelected ? [] : allVisibleKeys), className: "w-3.5 h-3.5 rounded border-border text-primary-600 focus:ring-primary-500 cursor-pointer" })), _jsx("span", { className: "w-4" }), _jsx("span", { className: "w-4" }), _jsx("span", { className: "w-5" }), _jsx(SortableHeader, { field: "key", label: "Key", className: "w-20", sortField: sortField, sortDirection: sortDirection, onSort: handleSort }), _jsx(SortableHeader, { field: "summary", label: "Summary", className: "flex-1", sortField: sortField, sortDirection: sortDirection, onSort: handleSort }), _jsx(SortableHeader, { field: "status", label: "Status", className: "w-24 justify-center", sortField: sortField, sortDirection: sortDirection, onSort: handleSort }), _jsx(SortableHeader, { field: "priority", label: "Priority", className: "w-20 justify-center", sortField: sortField, sortDirection: sortDirection, onSort: handleSort }), _jsx(SortableHeader, { field: "assignee", label: "Assignee", className: "w-28 justify-end", sortField: sortField, sortDirection: sortDirection, onSort: handleSort }), _jsx(SortableHeader, { field: "storyPoints", label: "SP", className: "w-12 justify-end", sortField: sortField, sortDirection: sortDirection, onSort: handleSort }), _jsx(SortableHeader, { field: "startDate", label: "Start Date", className: "w-24 justify-end", sortField: sortField, sortDirection: sortDirection, onSort: handleSort }), _jsx(SortableHeader, { field: "dueDate", label: "Due Date", className: "w-24 justify-end", sortField: sortField, sortDirection: sortDirection, onSort: handleSort }), _jsx(SortableHeader, { field: "progress", label: "Progress", className: "w-16 justify-center", sortField: sortField, sortDirection: sortDirection, onSort: handleSort }), _jsx("span", { className: "w-6" })] }), sortedEpics.map(epic => renderRow(epic, 0, sortedEpics)), sortedUnparented.length > 0 && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "px-3 py-2 bg-amber-50 border-b border-border flex items-center justify-between gap-2", children: [_jsxs("span", { className: "text-xs font-medium text-amber-700", children: ["Unparented Issues (", filteredUnparented.length, unparentedSearch ? ` of ${sortedUnparented.length}` : '', ")"] }), _jsxs("div", { className: "relative", children: [_jsx("svg", { className: "w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-amber-400", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" }) }), _jsx("input", { type: "text", value: unparentedSearch, onChange: e => setUnparentedSearch(e.target.value), placeholder: "Filter by key, summary, assignee...", className: "text-xs border border-amber-200 rounded pl-7 pr-7 py-1 bg-white text-text-primary w-56 focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400" }), unparentedSearch && (_jsx("button", { onClick: () => setUnparentedSearch(''), className: "absolute right-1.5 top-1/2 -translate-y-1/2 text-amber-400 hover:text-amber-600", "aria-label": "Clear search", children: _jsx("svg", { className: "w-3.5 h-3.5", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 18L18 6M6 6l12 12" }) }) }))] })] }), filteredUnparented.length > 0 ? (filteredUnparented.map(issue => renderRow(issue, 0, filteredUnparented))) : (_jsxs("div", { className: "px-4 py-3 text-xs text-text-tertiary text-center", children: ["No unparented issues match \u201C", unparentedSearch, "\u201D"] }))] })), epics.length === 0 && unparentedIssues.length === 0 && (_jsxs("div", { className: "px-6 py-12 text-center text-text-tertiary", children: [_jsx("p", { className: "text-lg font-medium", children: "No issues found" }), _jsx("p", { className: "text-sm mt-1", children: "Try adjusting your filters or sync project data first." })] })), contextMenu && (_jsx(PlanContextMenu, { issue: contextMenu.issue, position: contextMenu.position, onClose: () => setContextMenu(null), onAddChild: onAddChild, onOpenInJira: handleOpenInJira, onViewDetails: onViewDetails }))] }));
}
//# sourceMappingURL=PlanListView.js.map